// ===========================================================================
// Private notes about a regular: a person's own reminder of who one of their
// regulars is.
//
// As somebody collects regulars, names stop meaning anything: nicknames change,
// several are Lumi's own "Quiet Otter 42", and the chat that made two people
// regulars is long gone (the operator, 2026-10-05). So each person may give
// each of their regulars a private NICKNAME, shown in their list in place of
// the name the regular used, and keep one short NOTE about them.
//
// The shape is what the products people already use have settled on (docs/
// research/09-private-notes-about-regulars.md): Signal's nickname and note,
// Telegram's custom name and note, Discord's note -- a short label and a short
// free text, only for people you are connected to. Not a contact book: fixed
// fields go unused, and on a site with a 13-17 band, fields asking for a real
// name, an age, a town or a school would be a form for exactly the details
// nobody should be collecting about a child.
//
// Only the person who wrote them ever sees a nickname or a note: never the
// regular they are about. Lumi moderators can read and remove them, and the
// editor says so. Both are held to the rules a message is held to in the
// author's band when they are saved, the note is shorter in 13-17, and both
// live only as long as the two are regulars and go when either of them is
// forgotten.
//
// The wire shapes below are what the three packages agree on. ASCII only.
//
//   REGULARS row (ServerMsg.REGULARS, GET /api/regulars/list), the author's own:
//     nickname: string|null   what this person calls that regular
//     note:     string|null   the note this person keeps about them
//     noteAt:   ISO|null      when either was last saved
//   POST /api/regulars/note { person, nickname, text }   both '' deletes them
//     200 { ok: true, nickname: string|null, note: string|null }
//     400 { ok: false, reason: 'not_regulars'|'refused'|'too_long'|'bad_request'|'not_available', limit? }
//     429 { error: 'rate_limited' }
//   Console: GET /api/admin/regulars/notes?band=&q=&limit=
//            POST /api/admin/regulars/notes/delete { author, subject }
// ===========================================================================

/**
 * The longest note anybody may keep about one regular, by band. Shorter for
 * 13-17: a note is for remembering who somebody is, and a long free text about
 * a child is a dossier. 280 for adults sits in the middle of what comparable
 * products allow (Telegram 128, Signal 240, Grindr 250, Discord 500).
 */
export const REGULAR_NOTE_MAX_CHARS = Object.freeze({ adult: 280, minor: 140 });
/** What the author is told, in every tab they have open, when a moderator removes their note. */
export const NOTE_REMOVED_NOTICE = "A moderator removed a note you kept about one of your regulars, because it broke Lumi's rules. Notes follow the same rules as messages.";

/** The longest private nickname: a name, not a sentence (Signal allows 26). */
export const REGULAR_NICKNAME_MAX_CHARS = 30;

/** The note limit for a band; an unknown band gets the stricter one. */
export function noteLimitFor(band) {
  return band === 'adult' ? REGULAR_NOTE_MAX_CHARS.adult : REGULAR_NOTE_MAX_CHARS.minor;
}

/**
 * A note as it is stored: line endings made one kind, control characters out,
 * no more than one blank line in a row, and trimmed. Not cut: a note over the
 * band's limit is refused (too_long) rather than shortened behind the
 * person's back. An empty result means "no note".
 * @param {unknown} text
 * @returns {string}
 */
export function cleanNoteText(text) {
  if (typeof text !== 'string') return '';
  return text
    .replace(/\r\n?/g, '\n')
    .replace(/[\u0000-\u0008\u000B-\u001F\u007F]/g, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** A private nickname as stored: one line, single spaces, trimmed. '' for none. */
export function cleanNickname(text) {
  if (typeof text !== 'string') return '';
  return text.replace(/[\u0000-\u001F\u007F]/g, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * What the regulars list shows under a regular's name: the note's first line,
 * or '' when there is none.
 * @param {string|null|undefined} note
 */
export function notePreview(note) {
  return String(note || '').split('\n').map((s) => s.trim()).find(Boolean) || '';
}
